import { isProduction } from '@/js/config';
import {
    ANALYTICS_EVENTS,
    ANALYTICS_SOURCES,
    ONBOARDING_STEP_EVENTS,
    YANDEX_METRIKA_ID,
} from '@/js/constants/analytics';
import { resolveAnalyticsSource } from '@/js/analytics/source';

export { ANALYTICS_EVENTS, ANALYTICS_SOURCES };

const TRACKED_EVENTS_KEY_PREFIX = 'analytics:tracked-events';
const ANONYMOUS_SUBJECT_ID = 'anonymous';
const YM_SCRIPT_SRC = `https://mc.yandex.ru/metrika/tag.js?id=${YANDEX_METRIKA_ID}`;
const HIT_FLUSH_MS = 500;

const source = resolveAnalyticsSource();

let subjectId = ANONYMOUS_SUBJECT_ID;
let trackedEvents = null;

const createYmStub = () => {
    window.ym ??= (...args) => {
        (window.ym.a ??= []).push(args);
    };
    window.ym.l = Date.now();
};

const loadYmScript = () => {
    const isLoaded = Array.from(document.scripts).some((script) => script.src === YM_SCRIPT_SRC);

    if (isLoaded) {
        return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = YM_SCRIPT_SRC;
    document.head.append(script);
};

const getStorageKey = () => `${TRACKED_EVENTS_KEY_PREFIX}:${source}:${subjectId}`;

const readTrackedEvents = () => {
    try {
        return new Set(JSON.parse(localStorage.getItem(getStorageKey()) ?? '[]'));
    } catch {
        return new Set();
    }
};

const getTrackedEvents = () => {
    trackedEvents ??= readTrackedEvents();
    return trackedEvents;
};

const markTracked = (eventName) => {
    const events = getTrackedEvents();
    events.add(eventName);

    try {
        localStorage.setItem(getStorageKey(), JSON.stringify([...events]));
    } catch {
        return;
    }
};

const yandexMetrikaProvider = {
    init: () => {
        createYmStub();
        loadYmScript();
        window.ym(YANDEX_METRIKA_ID, 'init', {
            ssr: true,
            webvisor: true,
            clickmap: true,
            ecommerce: 'dataLayer',
            referrer: document.referrer,
            url: location.href,
            accurateTrackBounce: true,
            trackLinks: true,
        });
    },
    identify: (id) => {
        window.ym?.(YANDEX_METRIKA_ID, 'setUserID', id);
    },
    track: (eventName, params) => {
        window.ym?.(YANDEX_METRIKA_ID, 'reachGoal', eventName, params);
    },
};

const consoleAnalyticsProvider = {
    init: () => {},
    identify: (id) => {
        console.log('[analytics] identify', id);
    },
    track: (eventName, params) => {
        console.log('[analytics]', eventName, params);
    },
};

const analyticsProvider = isProduction ? yandexMetrikaProvider : consoleAnalyticsProvider;

export const initAnalytics = () => {
    analyticsProvider.init();
};

/**
 * Связывает визиты одного партнёра и включает дедупликацию целей по нему
 *
 * @param {string | number} id идентификатор анкеты студии
 */
export const identifyAnalytics = (id) => {
    const nextSubjectId = id ? String(id) : ANONYMOUS_SUBJECT_ID;

    if (nextSubjectId === subjectId) {
        return;
    }

    subjectId = nextSubjectId;
    trackedEvents = null;

    if (subjectId !== ANONYMOUS_SUBJECT_ID) {
        analyticsProvider.identify(subjectId);
    }
};

export const track = (eventName, params = {}) => {
    analyticsProvider.track(eventName, { ...params, source });
};

/**
 * Отправляет цель не чаще одного раза на партнёра
 *
 * @returns {boolean} была ли цель отправлена
 */
const trackOnce = (eventName, params = {}) => {
    if (getTrackedEvents().has(eventName)) {
        return false;
    }

    track(eventName, params);
    markTracked(eventName);

    return true;
};

export const trackRegistrationOpened = () => {
    track(ANALYTICS_EVENTS.ONBOARDING_REGISTRATION_STARTED);
};

export const trackOnboardingStepSaved = (step, apiStep) => {
    const eventName = ONBOARDING_STEP_EVENTS[step];

    if (eventName) {
        track(eventName);
    }

    if (apiStep === 'finished') {
        trackOnce(ANALYTICS_EVENTS.ONBOARDING_COMPLETED);
    }
};

export const trackOnceAndRedirect = (eventName, url, params = {}) => {
    const isSent = trackOnce(eventName, params);
    const redirect = () => window.location.assign(url);

    if (!isSent || !isProduction) {
        redirect();
        return;
    }

    window.setTimeout(redirect, HIT_FLUSH_MS);
};
